import {
  fetchAssessmentTier,
  updateAssessmentTier,
  type AssessmentTierResponse,
} from "@/modules/third-party-risk/requests/intake-form-v2";
import { IntakeReviewStepper } from "@/modules/third-party-risk/pages/intake-review/components/IntakeReviewStepper";
import { LoadingSpinner } from "@/shared/components/custom/spin";
import VendorLogo from "@/shared/components/VendorSearchAutocomplete/VendorLogo";
import { ZButton } from "@/shared/components/button";
import { addNotification } from "@/shared/states/notification";
import { Select, SelectItem } from "@nextui-org/react";
import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useAgentSessionData } from "../../states";

interface IdParams {
  id: string;
}

const TIER_OPTIONS = [
  { value: "1", label: "Tier 1 — Critical" },
  { value: "2", label: "Tier 2 — High" },
  { value: "3", label: "Tier 3 — Medium" },
  { value: "4", label: "Tier 4 — Low" },
];

const TIER_COLORS: Record<number, { bg: string; text: string }> = {
  1: { bg: "bg-[#FEE2E2]", text: "text-[#DC2626]" },
  2: { bg: "bg-[#FEF3C7]", text: "text-[#D97706]" },
  3: { bg: "bg-[#DBEAFE]", text: "text-[#2563EB]" },
  4: { bg: "bg-[#D1FAE5]", text: "text-[#059669]" },
};

export default function TieringStep() {
  const { id } = useParams<keyof IdParams>() as IdParams;
  const [searchParams, setSearchParams] = useSearchParams();
  const sessiondata = useAgentSessionData(id);

  const [tierData, setTierData] = useState<AssessmentTierResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const multiVendor = (sessiondata as any)?.multi_file_vendor_assessment;
  const steps = ["Form", "Tiering", "Launch"];
  const currentStep = 2;

  const fetchTier = useCallback(async () => {
    try {
      const data = await fetchAssessmentTier(id);
      setTierData(data);
    } catch (error) {
      console.error("Failed to fetch tier:", error);
      addNotification({
        type: "error",
        message: "Failed to load tier information. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchTier();
  }, [fetchTier]);

  const handleOverrideTier = async (tierValue: string) => {
    const numValue = Number(tierValue);
    if (!numValue || numValue === tierData?.tier_number) return;

    setIsUpdating(true);
    try {
      const data = await updateAssessmentTier(id, numValue);
      setTierData(data);
    } catch (error) {
      console.error("Failed to update tier:", error);
      addNotification({
        type: "error",
        message: "Failed to update tier. Please try again.",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleBack = () => {
    setSearchParams(prev => { prev.set("reviewStep", "1"); return prev; });
  };

  const handleContinue = () => {
    setSearchParams(prev => { prev.set("reviewStep", "3"); return prev; });
  };

  const tierColor = TIER_COLORS[tierData?.tier_number ?? 3] ?? TIER_COLORS[3];

  if (isLoading) {
    return (
      <div className="w-full h-full bg-[#FAFAFA] bg-opacity-90 flex items-center justify-center">
        <LoadingSpinner
          containerClassName="bg-transparent relative"
          className="mx-auto"
        />
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-[#FAFAFA] bg-opacity-90 flex flex-col">
      {/* Header */}
      <div className="flex items-center p-4 border-b border-[#E2E8F0] w-full bg-white">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <VendorLogo
            alt={multiVendor?.vendor_name || ""}
            src={multiVendor?.vendor_logo_url || ""}
            needsAuthToken
            className="border border-[#E2E8F0]"
          />
          <span className="font-semibold text-2xl leading-7 text-[#020618] truncate">
            {multiVendor?.vendor_name}{" "}
            {multiVendor?.product_name || ""}
          </span>
        </div>
        <IntakeReviewStepper currentStep={currentStep} steps={steps} />
        <div className="flex-1" />
      </div>

      {/* Content */}
      <div className="flex-1 w-full overflow-y-auto p-6">
        <div className="flex flex-col gap-6 p-6 bg-white rounded-lg shadow-[0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_rgba(0,0,0,0.06)]">
          {/* Title section */}
          <div className="flex flex-col gap-2">
            <h2 className="text-base font-semibold leading-6 text-[#0F172B]">Confirm Risk Tier</h2>
            <p className="text-sm font-normal leading-5 text-[#62748E]">
              Review the calculated inherent risk tier and assessment requirements
            </p>
          </div>

          {/* Calculated Tier Card */}
          <div className="flex flex-col gap-6 p-6 border border-[#E2E8F0] rounded-lg">
            <p className="text-sm font-semibold text-[#0F172B]">Calculated Tier</p>
            <div className="flex flex-col justify-center gap-2 p-4 bg-[#EFF6FF] rounded-lg w-[480.5px] max-w-full">
              <span className="text-base font-semibold leading-6 text-[#0F172B]">
                Tier {tierData?.tier_number}
              </span>
              <span className="text-xs leading-4 text-[#62748E]">Tier</span>
            </div>

            <div className="w-[172px]">
              <Select
                label="Override Tier"
                placeholder="Select tier"
                selectedKeys={tierData ? [String(tierData.tier_number)] : []}
                onChange={(e) => void handleOverrideTier(e.target.value)}
                isDisabled={isUpdating}
                size="sm"
                variant="bordered"
                classNames={{
                  trigger: "h-9 min-h-9 py-2 px-3 border-[#E2E8F0] rounded-md",
                }}
              >
                {TIER_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </Select>
            </div>
          </div>

          {/* Recommended Assessment Requirements Card */}
          <div className="flex flex-col gap-6 p-6 border border-[#E2E8F0] rounded-lg">
            <p className="text-sm font-semibold text-[#0F172B]">
              Recommended Assessment Requirements
            </p>
            <div className="flex flex-wrap gap-2">
              <div className="flex-1 flex flex-col gap-2 p-4 bg-[#F8FAFC] rounded-lg">
                <span className="text-base font-semibold text-[#0F172B]">
                  {tierData?.number_of_controls ?? 0}
                </span>
                <span className="text-xs text-[#62748E]">Controls</span>
              </div>
              <div className="flex-1 flex flex-col gap-2 p-4 bg-[#F8FAFC] rounded-lg">
                <span className="text-base font-semibold text-[#0F172B]">
                  {tierData?.number_of_questions ?? 0}
                </span>
                <span className="text-xs text-[#62748E]">Questions</span>
              </div>
              <div className="flex-1 flex flex-col gap-2 p-4 bg-[#F8FAFC] rounded-lg">
                <span className="text-base font-semibold text-[#0F172B]">
                  {tierData?.number_of_documents ?? 0}
                </span>
                <span className="text-xs text-[#62748E]">Documents</span>
              </div>
            </div>
          </div>

          {/* Buttons inside the card */}
          <div className="flex justify-between items-center gap-6">
            <ZButton
              variant="bordered"
              onClick={handleBack}
              className="h-10 px-4 py-2 border-[#006FEE] rounded-md text-base font-medium text-[#006FEE]"
            >
              Back
            </ZButton>
            <ZButton
              onClick={handleContinue}
              className="h-10 px-4 py-2 bg-[#006FEE] rounded-md text-base font-medium text-white"
            >
              Continue
            </ZButton>
          </div>
        </div>
      </div>
    </div>
  );
}
